import React, { useEffect, useMemo, useState } from "react";
import { api, mediaUrl, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  Search,
  Play,
  Square,
  X,
  Shuffle,
  MessageCircle,
  Video,
  Loader2,
  Clock,
  Hourglass,
  XCircle,
  Users,
  CheckCircle2,
  AlertCircle,
  Facebook,
  Instagram,
  Plug,
  RotateCcw,
} from "lucide-react";

const PAGE_SIZE = 8;

// Helper para ler/escrever no sessionStorage com fallback seguro
function usePersistedState(key, defaultValue) {
  const [state, setState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(key);
      if (saved === null) return defaultValue;
      return JSON.parse(saved);
    } catch {
      return defaultValue;
    }
  });

  const setPersistedState = (value) => {
    setState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      try {
        sessionStorage.setItem(key, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  return [state, setPersistedState];
}

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = usePersistedState("dashboard_selected", []);
  const [search, setSearch] = usePersistedState("dashboard_search", "");
  const [platformFilter, setPlatformFilter] = usePersistedState("dashboard_platform", "all");
  const [page, setPage] = usePersistedState("dashboard_page", 1);

  const [title, setTitle] = usePersistedState("dashboard_title", "");
  const [imageUrl, setImageUrl] = usePersistedState("dashboard_imageUrl", "");
  const [uploading, setUploading] = useState(false);
  const [threads, setThreads] = usePersistedState("dashboard_threads", 1);
  const [delay, setDelay] = usePersistedState("dashboard_delay", 120);

  const [lastCampaign, setLastCampaign] = useState(null);

  const [activeCampaign, setActiveCampaign] = useState(null);
  const [starting, setStarting] = useState(false);

  // Extensão é considerada "ativa" se deu sinal de vida (poll) nos últimos 2
  // minutos — ela consulta a fila a cada 30s quando está rodando de verdade.
  const extensionActive = useMemo(() => {
    if (!user?.extension_last_seen_at) return false;
    const lastSeen = new Date(user.extension_last_seen_at).getTime();
    return Date.now() - lastSeen < 2 * 60 * 1000;
  }, [user?.extension_last_seen_at]);

  useEffect(() => {
    api.get("/campaigns/last")
      .then(({ data }) => { if (data.found) setLastCampaign(data); })
      .catch(() => {});
  }, []);

  // O "user" só vinha carregado uma vez no login — se a extensão liga DEPOIS
  // disso, a tela nunca sabia. Atualiza já na entrada, e depois a cada 20s.
  useEffect(() => {
    refreshUser().catch(() => {});
    const i = setInterval(() => { refreshUser().catch(() => {}); }, 20000);
    return () => clearInterval(i);
  }, []);

  const usarTextoAnterior = () => {
    if (!lastCampaign) return;
    setTitle(lastCampaign.title || "");
    if (lastCampaign.image_url) setImageUrl(lastCampaign.image_url);
    toast.success("Texto da última publicação preenchido!");
  };

  // selected é array no storage, mas Set no uso — convertemos na inicialização
  const selectedSet = new Set(selected);

  useEffect(() => {
    loadGroups();
    loadActiveCampaign();
    const i = setInterval(loadActiveCampaign, 2500);
    return () => clearInterval(i);
  }, []);

  const loadGroups = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };

  const loadActiveCampaign = async () => {
    const { data } = await api.get("/campaigns");
    const running = data.find((c) => c.status === "running" || c.status === "queued");
    if (running) {
      const { data: full } = await api.get(`/campaigns/${running.id}`);
      setActiveCampaign(full);
    } else if (activeCampaign) {
      // refresh final state
      const { data: full } = await api.get(`/campaigns/${activeCampaign.campaign.id}`);
      setActiveCampaign(full);
      if (full.campaign.status === "completed" || full.campaign.status === "stopped") {
        setTimeout(() => setActiveCampaign(null), 5000);
      }
    }
  };

  const filtered = useMemo(() => {
    return groups.filter((g) => {
      if (platformFilter !== "all" && g.platform !== platformFilter) return false;
      if (search && !g.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [groups, platformFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (id) => {
    if (isRunning) return; // congelado durante a execução
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  };

  const toggleAll = () => {
    if (isRunning) return; // congelado durante a execução
    if (pageItems.every((g) => selectedSet.has(g.id))) {
      setSelected((prev) => {
        const s = new Set(prev);
        pageItems.forEach((g) => s.delete(g.id));
        return Array.from(s);
      });
    } else {
      setSelected((prev) => {
        const s = new Set(prev);
        pageItems.forEach((g) => s.add(g.id));
        return Array.from(s);
      });
    }
  };

  const handleUpload = async (e) => {
    if (isRunning) return; // congelado durante a execução
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.url);
      toast.success("Imagem carregada");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao enviar imagem"));
    } finally {
      setUploading(false);
    }
  };

  const insertRandom = (kind) => {
    if (isRunning) return; // congelado durante a execução
    const map = {
      T: "🎯 ",
      D: "\n\n📢 Oportunidade imperdível! ",
      M: "\n\n💬 Marque quem precisa ver isso! ",
    };
    setTitle((t) => t + map[kind]);
  };

  const startCampaign = async () => {
    if (!title.trim()) return toast.error("Digite a descrição da publicação");
    if (selectedSet.size === 0) return toast.error("Selecione ao menos 1 grupo/página");
    if (delay < 60) return toast.error("Delay mínimo é 60 segundos");

    setStarting(true);
    try {
      const { data: camp } = await api.post("/campaigns", {
        title,
        image_url: imageUrl || null,
        group_ids: Array.from(selectedSet),
        delay_seconds: Number(delay),
        threads: Number(threads),
      });
      await api.post(`/campaigns/${camp.id}/start`);
      toast.success(`Divulgação iniciada em ${selectedSet.size} destinos`);
      // Não limpa mais o formulário — ele fica congelado mostrando o que está
      // rodando, e só libera de novo quando a campanha terminar ou for parada.
      setActiveCampaign({ campaign: camp, items: [] });
      await loadActiveCampaign();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao iniciar"));
    } finally {
      setStarting(false);
    }
  };

  const stopCampaign = async () => {
    if (!activeCampaign) return;
    try {
      await api.post(`/campaigns/${activeCampaign.campaign.id}/stop`);
      toast.info("Divulgação parada");
      await loadActiveCampaign();
    } catch {
      toast.error("Erro ao parar");
    }
  };

  const isRunning = activeCampaign?.campaign?.status === "running" || activeCampaign?.campaign?.status === "queued";

  return (
    <>
      {!extensionActive && (
        <div className="fixed inset-0 z-[200] bg-zinc-900/70 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-sm max-w-md w-full p-8 text-center shadow-2xl">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <Plug className="h-7 w-7 text-amber-600" />
            </div>
            <h2 className="font-display text-xl font-bold tracking-tight mb-2">
              Necessário usar a extensão
            </h2>
            <p className="text-sm text-zinc-500 mb-6 leading-relaxed">
              Pra publicar nos seus grupos e páginas, a extensão do Chrome precisa estar instalada e
              conectada. Acesse a aba <strong>"Extensão"</strong> no menu lateral e siga as etapas
              dentro da página.
            </p>
            <Button onClick={() => navigate("/extensao")} className="rounded-sm bg-blue-600 hover:bg-blue-700 w-full">
              Ir para a Extensão
            </Button>
          </div>
        </div>
      )}
    <div className="p-6 md:p-8 max-w-[1600px]">
      {/* Top header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono-label text-zinc-500 mb-1">Painel de Controle</p>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="dashboard-title">
            Nova Divulgação
          </h1>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <Metric label="Selecionados" value={selectedSet.size} />
          <Metric label="Disponíveis" value={filtered.length} />
          <Metric
            label="Tempo estimado"
            value={`${Math.ceil((selectedSet.size * delay) / Math.max(1, threads) / 60)} min`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT - Composer */}
        <section className="lg:col-span-5 space-y-4">
          <div className="bg-white border border-zinc-200 rounded-sm">
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 bg-green-500 rounded-sm flex items-center justify-center">
                  <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
                </div>
                <span className="text-sm font-medium">Publicação</span>
              </div>
              {title && !isRunning && (
                <button
                  onClick={() => setTitle("")}
                  className="text-zinc-400 hover:text-zinc-900"
                  data-testid="composer-clear-title"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {lastCampaign && !isRunning && (
              <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono-label text-zinc-400 mb-1">Última publicação</div>
                  <p className="text-xs text-zinc-500 line-clamp-2">{lastCampaign.title}</p>
                </div>
                <button
                  onClick={usarTextoAnterior}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-white border border-blue-200 rounded-sm px-2.5 py-1.5"
                  data-testid="composer-reuse-last-btn"
                >
                  <RotateCcw className="h-3 w-3" /> Usar este texto
                </button>
              </div>
            )}
            <Textarea
              data-testid="composer-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isRunning}
              placeholder="Digite o texto da sua publicação...&#10;&#10;Exemplo:&#10;🔥 VAGA DE EMPREGO ABERTA&#10;Cargo: Motorista Torton&#10;Salário: R$ 5.684&#10;Contato: (81) 2984-0491"
              className="min-h-[180px] border-0 rounded-none focus-visible:ring-0 resize-none font-body disabled:bg-zinc-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Image */}
          <div className="bg-white border border-zinc-200 rounded-sm">
            {imageUrl ? (
              <div className="relative">
                <img
                  src={mediaUrl(imageUrl)}
                  alt="Preview"
                  className="w-full h-auto max-h-[320px] object-contain bg-zinc-50"
                />
                <button
                  onClick={() => setImageUrl("")}
                  disabled={isRunning}
                  className="absolute top-2 right-2 h-8 w-8 bg-zinc-900/80 hover:bg-zinc-900 text-white rounded-sm flex items-center justify-center disabled:opacity-0 disabled:pointer-events-none"
                  data-testid="composer-remove-image"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center py-12 transition-colors ${isRunning ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-zinc-50"}`}>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUpload}
                  disabled={isRunning}
                  data-testid="composer-upload-input"
                />
                {uploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                ) : (
                  <>
                    <ImageIcon className="h-8 w-8 text-zinc-400 mb-2" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Adicionar Foto</span>
                    <span className="text-xs text-zinc-500 mt-1">PNG, JPG ou WEBP até 10 MB</span>
                  </>
                )}
              </label>
            )}
          </div>

          {/* Quick actions */}
          <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <QuickBtn label="RANDOM T" onClick={() => insertRandom("T")} testid="random-t-btn" disabled={isRunning} />
              <QuickBtn label="RANDOM D" onClick={() => insertRandom("D")} testid="random-d-btn" disabled={isRunning} />
              <QuickBtn label="RANDOM M" onClick={() => insertRandom("M")} testid="random-m-btn" disabled={isRunning} />
            </div>
            <div className="flex items-center gap-2">
              <IconBtn icon={MessageCircle} color="text-blue-500" title="Comentário" />
              <IconBtn icon={ImageIcon} color="text-green-500" title="Imagem" />
              <IconBtn icon={Video} color="text-red-500" title="Vídeo" />
            </div>
          </div>

          {/* Thread + Delay */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <Label className="font-mono-label text-zinc-500 mb-2 block">
                Thread (Recomendado 1)
              </Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={threads}
                onChange={(e) => setThreads(e.target.value)}
                disabled={isRunning}
                data-testid="composer-threads-input"
                className="rounded-sm h-11 text-base font-mono disabled:bg-zinc-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <Label className="font-mono-label text-zinc-500 mb-2 block">
                Delay (min. 60s)
              </Label>
              <Input
                type="number"
                min={60}
                max={3600}
                value={delay}
                onChange={(e) => setDelay(e.target.value)}
                disabled={isRunning}
                data-testid="composer-delay-input"
                className="rounded-sm h-11 text-base font-mono disabled:bg-zinc-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Start/Stop */}
          {isRunning ? (
            <Button
              onClick={stopCampaign}
              data-testid="composer-stop-button"
              className="w-full h-14 rounded-sm bg-red-600 hover:bg-red-700 text-white font-bold text-base tracking-wide"
            >
              <Square className="h-5 w-5 mr-2 fill-white" />
              PARAR AUTOMAÇÃO
            </Button>
          ) : (
            <Button
              onClick={startCampaign}
              disabled={starting || selectedSet.size === 0}
              data-testid="composer-start-button"
              className="w-full h-14 rounded-sm bg-blue-600 hover:bg-blue-700 text-white font-bold text-base tracking-wide"
            >
              {starting ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Play className="h-5 w-5 mr-2 fill-white" />
              )}
              INICIAR DIVULGAÇÃO · {selectedSet.size} DESTINOS
            </Button>
          )}

          {activeCampaign && <LiveProgress data={activeCampaign} />}
          {activeCampaign && <ItemsChecklist items={activeCampaign.items || []} />}
        </section>

        {/* RIGHT - Groups */}
        <section className="lg:col-span-7">
          <div className="bg-white border border-zinc-200 rounded-sm">
            <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Pesquisar grupos ou páginas..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  data-testid="groups-search-input"
                  className="pl-10 rounded-sm h-10"
                />
              </div>
              <div className="flex gap-1 bg-zinc-100 p-0.5 rounded-sm">
                <FilterBtn active={platformFilter === "all"} onClick={() => setPlatformFilter("all")} testid="filter-all">
                  Todos
                </FilterBtn>
                <FilterBtn
                  active={platformFilter === "facebook_group"}
                  onClick={() => setPlatformFilter("facebook_group")}
                  testid="filter-fb-groups"
                >
                  <Facebook className="h-3.5 w-3.5" />
                  Grupos
                </FilterBtn>
                <FilterBtn
                  active={platformFilter === "facebook_page"}
                  onClick={() => setPlatformFilter("facebook_page")}
                  testid="filter-fb-pages"
                >
                  <Facebook className="h-3.5 w-3.5" />
                  Páginas
                </FilterBtn>
                <FilterBtn
                  active={platformFilter === "instagram"}
                  onClick={() => setPlatformFilter("instagram")}
                  testid="filter-ig"
                >
                  <Instagram className="h-3.5 w-3.5" />
                  Instagram
                </FilterBtn>
              </div>
            </div>

            <div className="px-5 py-2.5 border-b border-zinc-100 flex items-center justify-between text-xs">
              <label className={`flex items-center gap-2 ${isRunning ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                <Checkbox
                  checked={pageItems.length > 0 && pageItems.every((g) => selectedSet.has(g.id))}
                  onCheckedChange={toggleAll}
                  disabled={isRunning}
                  data-testid="groups-select-all-checkbox"
                />
                <span className="font-mono-label text-zinc-600">
                  Selecionar página atual
                </span>
              </label>
              <span className="font-mono-label text-zinc-500">
                {selectedSet.size} selecionados · {filtered.length} totais
              </span>
            </div>

            <div className={`divide-y divide-zinc-100 max-h-[560px] overflow-y-auto scroll-thin ${isRunning ? "opacity-60" : ""}`}>
              {pageItems.length === 0 ? (
                <div className="py-16 text-center text-zinc-500 text-sm">
                  Nenhum grupo/página encontrado
                </div>
              ) : (
                pageItems.map((g, idx) => (
                  <div
                    key={g.id}
                    className={`flex items-center gap-4 px-5 py-3 transition-colors fade-in ${isRunning ? "cursor-not-allowed" : "hover:bg-zinc-50 cursor-pointer"}`}
                    style={{ animationDelay: `${idx * 20}ms` }}
                    onClick={() => toggle(g.id)}
                    data-testid={`group-row-${g.id}`}
                  >
                    <Checkbox
                      checked={selectedSet.has(g.id)}
                      onCheckedChange={() => toggle(g.id)}
                      disabled={isRunning}
                      data-testid={`group-item-checkbox-${g.id}`}
                    />
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 flex-shrink-0 flex items-center justify-center font-display font-bold text-zinc-600 text-sm">
                      {g.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{g.name}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">{g.fb_id}</div>
                    </div>
                    <PlatformBadge platform={g.platform} />
                  </div>
                ))
              )}
            </div>

            <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between text-sm">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="groups-prev-page"
                className="font-mono-label text-zinc-600 hover:text-zinc-900 disabled:opacity-30"
              >
                « ANTERIOR
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                  const p = i + Math.max(1, Math.min(page - 2, totalPages - 4));
                  if (p > totalPages) return null;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      data-testid={`groups-page-${p}`}
                      className={`min-w-[32px] h-8 px-2 text-sm rounded-sm transition-colors ${
                        page === p
                          ? "bg-blue-600 text-white font-medium"
                          : "text-zinc-600 hover:bg-zinc-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                {totalPages > 5 && page < totalPages - 2 && (
                  <>
                    <span className="text-zinc-400 px-1">…</span>
                    <button
                      onClick={() => setPage(totalPages)}
                      className="min-w-[32px] h-8 px-2 text-sm rounded-sm text-zinc-600 hover:bg-zinc-100"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="groups-next-page"
                className="font-mono-label text-zinc-600 hover:text-zinc-900 disabled:opacity-30"
              >
                PRÓXIMA »
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
    </>
  );
}

const Metric = ({ label, value }) => (
  <div>
    <div className="font-mono-label text-zinc-400">{label}</div>
    <div className="font-display text-xl font-bold tabular-nums mt-0.5">{value}</div>
  </div>
);

const QuickBtn = ({ label, onClick, testid, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="h-8 px-3 rounded-sm border border-zinc-200 text-xs font-medium font-mono text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 transition-colors inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
  >
    <Shuffle className="h-3 w-3" />
    {label}
  </button>
);

const IconBtn = ({ icon: Icon, color, title }) => (
  <button
    title={title}
    className="h-8 w-8 rounded-sm border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center transition-colors"
  >
    <Icon className={`h-4 w-4 ${color}`} strokeWidth={2} />
  </button>
);

const FilterBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`h-8 px-3 rounded-sm text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
      active ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900"
    }`}
  >
    {children}
  </button>
);

const PlatformBadge = ({ platform }) => {
  if (platform === "instagram") {
    return (
      <Badge variant="outline" className="rounded-sm font-mono-label border-pink-200 text-pink-700 bg-pink-50">
        <Instagram className="h-3 w-3 mr-1" /> IG
      </Badge>
    );
  }
  if (platform === "facebook_page") {
    return (
      <Badge variant="outline" className="rounded-sm font-mono-label border-blue-200 text-blue-700 bg-blue-50">
        <Facebook className="h-3 w-3 mr-1" /> Página
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="rounded-sm font-mono-label border-zinc-200 text-zinc-700">
      <Facebook className="h-3 w-3 mr-1" /> Grupo
    </Badge>
  );
};

function LiveProgress({ data }) {
  const c = data.campaign;
  const items = data.items || [];
  const total = c.total || 0;
  const sent = c.sent || 0;
  const failed = c.failed || 0;
  const done = sent + failed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const current = items.find((it) => it.status === "sending");

  return (
    <div className="bg-zinc-950 text-white rounded-sm p-5 space-y-3" data-testid="live-progress-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 bg-green-400 rounded-full animate-status-pulse" />
          <span className="font-mono-label text-green-400">
            {c.status === "running" ? "EXECUTANDO" : c.status === "completed" ? "CONCLUÍDO" : c.status === "stopped" ? "PARADO" : "NA FILA"}
          </span>
        </div>
        <span className="font-mono text-xs text-zinc-400">{pct}%</span>
      </div>

      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-green-400 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="grid grid-cols-4 gap-2 pt-2 text-xs">
        <div>
          <div className="font-mono-label text-zinc-500">Total</div>
          <div className="font-display text-lg font-bold">{total}</div>
        </div>
        <div>
          <div className="font-mono-label text-green-400">Enviados</div>
          <div className="font-display text-lg font-bold text-green-400">{sent}</div>
        </div>
        <div>
          <div className="font-mono-label text-red-400">Falhas</div>
          <div className="font-display text-lg font-bold text-red-400">{failed}</div>
        </div>
        <div>
          <div className="font-mono-label text-zinc-500">Restantes</div>
          <div className="font-display text-lg font-bold">{total - done}</div>
        </div>
      </div>

      {current && (
        <div className="pt-3 border-t border-zinc-800 flex items-center gap-2 text-xs">
          <Loader2 className="h-3 w-3 animate-spin text-green-400" />
          <span className="text-zinc-400">Publicando em:</span>
          <span className="font-medium truncate">{current.group_name}</span>
        </div>
      )}
    </div>
  );
}

function ChecklistSection({ title, icon: Icon, colorClass, items, emptyHint }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm">
      <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${colorClass}`} />
        <span className="text-sm font-medium">{title}</span>
        <span className="font-mono text-xs text-zinc-400 ml-auto">{items.length}</span>
      </div>
      <div className="max-h-[180px] overflow-y-auto scroll-thin">
        {items.length === 0 ? (
          <div className="py-4 px-4 text-xs text-zinc-400">{emptyHint}</div>
        ) : (
          items.map((it) => (
            <div key={it.id} className="px-4 py-2 flex items-center justify-between gap-2 border-b border-zinc-50 last:border-0">
              <span className="text-sm truncate">{it.group_name}</span>
              {it.error && (
                <span className="text-xs text-zinc-400 truncate max-w-[140px]" title={it.error}>
                  {it.error}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ItemsChecklist({ items }) {
  const naFila = items.filter((it) => it.status === "pending" || it.status === "sending");
  const enviados = items.filter((it) => it.status === "sent");
  const falhas = items.filter((it) => it.status === "failed");
  const pendenteAprovacao = items.filter((it) => it.status === "pending_approval");

  if (items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="items-checklist">
      <ChecklistSection
        title="Na fila"
        icon={Clock}
        colorClass="text-zinc-400"
        items={naFila}
        emptyHint="Nenhum grupo na fila"
      />
      <ChecklistSection
        title="Enviados"
        icon={CheckCircle2}
        colorClass="text-green-500"
        items={enviados}
        emptyHint="Ainda nenhum envio confirmado"
      />
      <ChecklistSection
        title="Pendente de aprovação"
        icon={Hourglass}
        colorClass="text-amber-500"
        items={pendenteAprovacao}
        emptyHint="Nenhum post em análise do administrador do grupo (em breve detectamos isso automaticamente)"
      />
      <ChecklistSection
        title="Falhas"
        icon={XCircle}
        colorClass="text-red-500"
        items={falhas}
        emptyHint="Nenhuma falha"
      />
    </div>
  );
}
